/* eslint-disable max-lines-per-function */

import { useUpdateEffect } from 'react-use';

import { PractitionerView } from '@dreryk/egabinet-personnel-frontend-api';

import { Translate, useFormController, useTranslate } from '@dreryk/react-common';
import {
   Checkbox,
   FormLabel,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   TextView,
   Tooltip,
} from '@dreryk/react-components';

import { practitionersTranslationKeys } from '../../../translations';

import { StyledFormRow } from './PractitionerUpdatePermissionsForm.styled';
import {
   PractitionerUpdatePermissionsFormBean,
   PractitionerUpdatePermissionsFormController,
} from './PractitionerUpdatePermissionsForm.types';
import { EmailTextView } from './shared/EmailTextView';
import { PhoneNumberTextView } from './shared/PhoneNumberTextView';

export type PractitionerUpdatePermissionsFormRenderProps = Pick<
   PractitionerUpdatePermissionsFormController,
   'handleSubmit' | 'formState'
>;

type PractitionerUpdatePermissionsFormProps = {
   readonly children: (renderProps: PractitionerUpdatePermissionsFormRenderProps) => JSX.Element;
   readonly practitioner: PractitionerView;
};

export const PractitionerUpdatePermissionsForm = (props: PractitionerUpdatePermissionsFormProps): JSX.Element => {
   const {
      formState,
      handleSubmit,
      register,
      watch,
      setValue,
   } = useFormController<PractitionerUpdatePermissionsFormBean>({
      addingMedicalData: props.practitioner.permissions.addingMedicalData,
      medicalDataPreview: props.practitioner.permissions.medicalDataPreview,
      conductingPreInterview: props.practitioner.permissions.conductingPreInterview,
   });

   const translate = useTranslate();
   const medicalLicenceNumberAvailable = props.practitioner.medicalLicenceNumber;
   const peselAvailable = !medicalLicenceNumberAvailable && props.practitioner.pesel;
   const { medicalDataPreview, addingMedicalData } = watch(['medicalDataPreview', 'addingMedicalData']);
   const medicalDataDisabled = props.practitioner.jobPositions.includes('DOCTOR');

   useUpdateEffect(() => {
      if (!medicalDataPreview) {
         setValue('addingMedicalData', false);
         setValue('conductingPreInterview', false);
      }

      if (!addingMedicalData) {
         setValue('conductingPreInterview', false);
      }
   }, [medicalDataPreview, addingMedicalData]);

   return (
      <StyledFlex direction='column' gap='24px' as='form' noValidate>
         <StyledFormRow>
            {medicalLicenceNumberAvailable ? (
               <TextView label={practitionersTranslationKeys.modal.medicalLicenceNumber} testId='medicalLicenceNumber'>
                  <div>{props.practitioner.medicalLicenceNumber?.getValue() ?? '-'}</div>
               </TextView>
            ) : null}
            {peselAvailable ? (
               <TextView label={practitionersTranslationKeys.modal.pesel} testId='pesel'>
                  <div>{props.practitioner.pesel?.asString() ?? '-'}</div>
               </TextView>
            ) : null}
         </StyledFormRow>
         <StyledFormRow>
            <TextView label={practitionersTranslationKeys.modal.firstName} testId='firstName'>
               <div>{props.practitioner.firstName.asString()}</div>
            </TextView>
            <TextView label={practitionersTranslationKeys.modal.lastName} testId='lastName'>
               <div>{props.practitioner.lastName.asString()}</div>
            </TextView>
         </StyledFormRow>
         <StyledFormRow>
            <PhoneNumberTextView value={props.practitioner.phoneNumber} />
            <EmailTextView value={props.practitioner.emailAddress} />
         </StyledFormRow>
         <StyledFormRow>
            <TextView label={practitionersTranslationKeys.modal.role} testId='role'>
               {props.practitioner.jobPositions
                  .map((position) => translate(`${practitionersTranslationKeys.jobPositions[position]}`))
                  .join(', ')}
            </TextView>
         </StyledFormRow>

         <StyledFlex direction='column' gap='8px'>
            <FormLabel label={practitionersTranslationKeys.modal.permissions} testId={'checkboxGroupLabel'} />
            <Checkbox
               testId={'medicalDataPreview'}
               label={practitionersTranslationKeys.modal.medicalDataPreview}
               controller={register('medicalDataPreview')}
               disabled={medicalDataDisabled}
            />
            <Checkbox
               testId={'addingMedicalData'}
               label={practitionersTranslationKeys.modal.addingMedicalData}
               controller={register('addingMedicalData')}
               disabled={medicalDataDisabled || !medicalDataPreview}
            />
            <Checkbox
               testId={'conductingPreInterview'}
               label={
                  <StyledFlex gap={'6px'}>
                     <Translate>{practitionersTranslationKeys.modal.conductingPreInterview}</Translate>
                     <Tooltip
                        testId={'conductingPreInterviewHint'}
                        content={practitionersTranslationKeys.modal.conductingPreInterviewHint}
                     >
                        <IconWrapper
                           testId={'conductingPreInterviewHint'}
                           size={IconSize.SMALL}
                           fillColor={IconFillColor.PRIMARY}
                        >
                           <QuestionmarkCircleIcon />
                        </IconWrapper>
                     </Tooltip>
                  </StyledFlex>
               }
               controller={register('conductingPreInterview')}
               disabled={!addingMedicalData}
            />
            {props.children({
               formState: formState,
               handleSubmit: handleSubmit,
            })}
         </StyledFlex>
      </StyledFlex>
   );
};
